import {ChangeDetectorRef, Component, ComponentFactoryResolver, Input} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FieldRecord} from '../../../model/api/field-record';
import {FormFieldConfig} from '../../../model/api/form-field-config';
import {CommonFieldService} from '../../../services/common/common-field.service';
import {CrudBaseComponent} from '../../../crystal-elements/crud/crud-base.component';
import {UserInfoStorageService} from '../../../services/auth/user-info-storage.service';
import TYPE = FormFieldConfig.TYPE;
import {SchedulerService} from '../../../services/tools/scheduler.service';
import {ScheduleProfile} from '../../../model/tools/schedule-profile';
import DBTableDetail = ScheduleProfile.DBTableDetail;
import {ScheduleProperties} from '../../../model/tools/schedule-properties';
import {FormFieldGroupConfig} from '../../../model/api/form-field-group-config';
import dateFormat from "dateformat";
import ArgProfile = ScheduleProfile.ArgProfile;
import {AppConfigService} from "../../../services/app-config.service";

@Component({
  templateUrl: './scheduler-view.component.html',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class SchedulerViewComponent extends CrudBaseComponent<ScheduleProfile> {

  // fields
  alertTypes: FieldRecord<any>[] = [];
  logScanModes: FieldRecord<any>[] = [];

  settings: ScheduleProperties;
  settingsSubmitted: boolean = false;
  settingsDialogFormVisible: boolean = false;

  settingsFormConfig: FormFieldGroupConfig[] = [];

  constructor(cdref: ChangeDetectorRef,
              schedulerService: SchedulerService,
              commonFieldService: CommonFieldService,
              private userInfoStorageService: UserInfoStorageService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService,
              configService: AppConfigService) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.crudService = schedulerService;
    this.fieldId = 'title';
    this.transPartKey = 'tools.scheduler';
    this.prepareForCreate = this.prepareRecordForCreate;
    this.prepareForClone = this.prepareRecordForClone;
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'active',
        width: 50,
        type: 'BUTTON',
        class: value => 'centered',
        icon: value => value.active ? 'fas fa-toggle-on record-enabled' : 'fas fa-toggle-off record-disabled',
        command: (record: ScheduleProfile) => {
          this.activate(record);
        },
        mapId: value => '',
      },
      {
        field: 'title',
        width: 200
      },
      {
        field: 'cron',
      }
    ];

    this.formConfig = this.getFormConfig();
    this.settingsFormConfig = this.getSettingsFormConfig();
  }

  protected postInitConfigs() {
    this.headerButtonConfig.push(
      {
        separator: true,
      },
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.execute_button'),
        'icon': 'fas fa-play',
        class: ' p-button-info',
        command: records => this.execute(records),
        enabled: () => this.hasMultipleSelection()
      },
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.refresh_button'),
        'icon': 'fas fa-sync-alt',
        class: ' p-button-success',
        command: records => this.refresh(),
        enabled: () => true
      },
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.settings_button'),
        'icon': 'fas fa-sliders-h',
        class: ' p-button-success',
        command: records => this.editSettings(),
        enabled: () => true
      }
    );
    this.headerButtonConfig = [...this.headerButtonConfig];

    this.contextMenuConfig.unshift(
      {
        label: this.translateService.instant('page.' + this.transPartKey + '.table.execute_button'),
        icon: 'fas fa-play',
        command: () => this.execute([this.formRecord]),
      },
      {
        separator: true
      }
    );
    this.contextMenuConfig = [...this.contextMenuConfig];
  }

  private execute(records: ScheduleProfile[]) {

    this.tableLoading = true;
    this.confirmationService.confirm({
      header: this.getTranslation('page.' + this.transPartKey + '.table.execute_button')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's',
      message: (this.getTranslation('page.' + this.transPartKey + '.dialog.confirm_execute') + '?'),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation('page.' + this.transPartKey + '.table.execute_button'),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectLabel: this.getTranslation('common.dialog.cancel'),
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => {
        const titles = [];
        records.forEach(value => {
            titles.push(value.title);
          }
        );
        (this.crudService as SchedulerService).execute(titles).subscribe(apiFileResponse => {
          if (apiFileResponse.success) {
            this.infoMessage(JSON.stringify(apiFileResponse.item));
          } else {
            this.errorMessage(apiFileResponse.message);
          }
          this.tableLoading = false;
        });
      },
      reject: () => {
        this.tableLoading = false;
      }
    });
  }

  private refresh() {
    this.tableLoading = true;
    this.confirmationService.confirm({
      header: this.getTranslation('page.' + this.transPartKey + '.table.refresh_button')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's',
      message: (this.getTranslation('page.' + this.transPartKey + '.dialog.confirm_refresh') + '?'),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation('page.' + this.transPartKey + '.table.refresh_button'),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectLabel: this.getTranslation('common.dialog.cancel'),
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => {
        (this.crudService as SchedulerService).refresh().subscribe(apiFileResponse => {
          if (apiFileResponse.success) {
            this.logMessage(apiFileResponse.message);
          } else {
            this.errorMessage(apiFileResponse.message);
          }
          this.tableLoading = false;
        });
      },
      reject: () => {
        this.tableLoading = false;
      }
    });

  }

  private editSettings() {
    this.settingsDialogFormVisible = true;
  }

  saveSettings() {
    (this.crudService as SchedulerService).saveSettings(this.settings).subscribe(apiFileResponse => {
      if (apiFileResponse.success) {
        this.settingsDialogFormVisible = false;
      } else {
        this.errorMessage(apiFileResponse.message);
      }
      this.tableLoading = false;
    });
  }


  getFormConfig() {
    return [
      {
        group: '',
        fields: [
          {
            field: 'title',
            required: true,
            column: 3,
            type: TYPE.TEXT,
            validate: formRecord => {
              if (formRecord.originTitle !== formRecord.title && this.records
                && this.records.filter(function (record) {
                  return record.title === formRecord.title;
                }).length > 0) {
                return this.getTranslation('page.tools.scheduler.message.titleMustBeUnique');
              }
              return '';
            }
          },
          {
            field: 'cron',
            column: 3,
            type: TYPE.CRON,
          },
          {
            field: 'active',
            column: 2,
            type: TYPE.CHECKBOX,
          },
          {
            field: 'uiShortCut',
            column: 2,
            type: TYPE.CHECKBOX,
          },
          {
            column: 2,
            type: TYPE.NONE,
          },
          {
            field: 'alert',
            column: 2,
            type: TYPE.CHECKBOX,
          },
          {
            field: 'alertLimitOneADay',
            column: 2,
            type: TYPE.CHECKBOX,
          },
          {
            field: 'alertType',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.alertTypes
          },
          {
            field: 'alertExpectedExitCodes',
            column: 4,
            type: TYPE.MULTITEXT,
          },
          {
            field: 'alertMailTemplate',
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'alertSubject',
            column: 9,
            type: TYPE.TEXTAREA,
          },
          {
            field: 'alertRecipients',
            column: 12,
            type: TYPE.MULTITEXT
          },
          //////////////// argCmds ////////////////
          {
            field: 'argCmds',
            column: 6,
            type: TYPE.RICHFIELD,
            richFieldConfig: {
              fieldId: 'title',
              fieldName: 'title',
              tableConfigs: [
                {
                  field: 'active',
                  width: 50,
                  mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
                },
                {
                  field: 'title',
                  width: 200
                },
                {
                  field: 'order',
                  width: 50
                },
                {
                  field: 'cmdLines',
                  mapId: record => {
                    return record['cmdLines'] ? record['cmdLines'].join(', ') : '';
                  }
                }
              ],
              fields: [
                {
                  group: '',
                  fields: [
                    {
                      field: 'active',
                      column: 2,
                      type: TYPE.CHECKBOX,
                    },
                    {
                      field: 'title',
                      required: true,
                      column: 4,
                      type: TYPE.TEXT,
                    },
                    {
                      field: 'order',
                      column: 2,
                      type: TYPE.INTEGER
                    },
                    {
                      column: 4,
                      type: TYPE.NONE
                    },
                    {
                      field: 'cmdLines',
                      column: 12,
                      type: TYPE.MULTITEXT
                    },
                    {
                      field: 'workingDir',
                      column: 12,
                      type: TYPE.TEXT
                    }]
                }]
            }
          },
          //////////////// argUpdateProps ////////////////
          {
            field: 'argUpdateProps',
            column: 6,
            type: TYPE.RICHFIELD,
            richFieldConfig: {
              fieldId: 'title',
              fieldName: 'title',
              tableConfigs: [
                {
                  field: 'active',
                  width: 50,
                  mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
                },
                {
                  field: 'title',
                  width: 200
                },
                {
                  field: 'order',
                  width: 50
                },
                {
                  field: 'source',
                },
              ],
              fields: [
                {
                  group: '',
                  fields: [
                    {
                      field: 'active',
                      column: 2,
                      type: TYPE.CHECKBOX,
                    },
                    {
                      field: 'title',
                      required: true,
                      column: 4,
                      type: TYPE.TEXT,
                    },
                    {
                      field: 'order',
                      column: 2,
                      type: TYPE.INTEGER
                    },
                    {
                      column: 4,
                      type: TYPE.NONE
                    },
                    {
                      field: 'source',
                      column: 6,
                      type: TYPE.TEXT
                    }]
                }]
            }
          },
          //////////////// argSQLCmds ////////////////
          {
            field: 'argSQLCmds',
            column: 12,
            type: TYPE.RICHFIELD,
            richFieldConfig: {
              fieldId: 'title',
              fieldName: 'title',
              tableConfigs: [
                {
                  field: 'active',
                  width: 50,
                  mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
                },
                {
                  field: 'title',
                  width: 200
                },
                {
                  field: 'order',
                  width: 50
                },
                {
                  field: 'dbUrl',
                  width: 250
                },
                {
                  field: 'dbQuery'
                },
                {
                  field: 'expectedResult'
                }
              ],
              fields: [
                {
                  group: '',
                  fields: [
                    {
                      field: 'active',
                      column: 2,
                      type: TYPE.CHECKBOX,
                    },
                    {
                      field: 'title',
                      required: true,
                      column: 4,
                      type: TYPE.TEXT,
                    },
                    {
                      field: 'order',
                      column: 2,
                      type: TYPE.INTEGER
                    },
                    {
                      column: 4,
                      type: TYPE.NONE
                    },
                    {
                      field: 'dbDriver',
                      column: 4,
                      type: TYPE.TEXT
                    },
                    {
                      field: 'dbUrl',
                      column: 4,
                      type: TYPE.TEXT
                    },
                    {
                      field: 'dbUsername',
                      column: 2,
                      type: TYPE.TEXT
                    },
                    {
                      field: 'dbPassword',
                      column: 2,
                      type: TYPE.TEXT
                    },
                    {
                      field: 'dbQuery',
                      column: 12,
                      type: TYPE.TEXTAREA
                    },
                    {
                      field: 'expectedResult',
                      column: 12,
                      type: TYPE.TEXT
                    }]
                }]
            }
          },
          //////////////// argExtractFiles ////////////////
          {
            field: 'argExtractFiles',
            column: 12,
            type: TYPE.RICHFIELD,
            richFieldConfig: {
              fieldId: 'title',
              fieldName: 'title',
              tableConfigs: [
                {
                  field: 'active',
                  width: 50,
                  mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
                },
                {
                  field: 'title',
                  width: 200
                },
                {
                  field: 'order',
                  width: 50
                },
                {
                  field: 'source',
                  width: 250
                },
                {
                  field: 'dest',
                }
              ],
              fields: [
                {
                  group: '',
                  fields: [
                    {
                      field: 'active',
                      column: 2,
                      type: TYPE.CHECKBOX,
                    },
                    {
                      field: 'title',
                      required: true,
                      column: 4,
                      type: TYPE.TEXT,
                    },
                    {
                      field: 'order',
                      column: 2,
                      type: TYPE.INTEGER
                    },
                    {
                      column: 4,
                      type: TYPE.NONE
                    },
                    {
                      field: 'source',
                      column: 6,
                      type: TYPE.TEXT
                    },
                    {
                      field: 'dest',
                      column: 6,
                      type: TYPE.TEXT
                    }]
                }]
            }
          },
          //////////////// ArgLogsAnalyze ////////////////
          {
            field: 'argLogsAnalyses',
            column: 12,
            type: TYPE.RICHFIELD,
            richFieldConfig: this.getArgLogsAnalyzeRichFieldConfig()
          },
          //////////////// argDBSyncs ////////////////
          {
            field: 'argDBSyncs',
            column: 12,
            type: TYPE.RICHFIELD,
            richFieldConfig: {
              fieldId: 'title',
              fieldName: 'title',
              tableConfigs: [
                {
                  field: 'active',
                  width: 50,
                  mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
                },
                {
                  field: 'title',
                  width: 200
                },
                {
                  field: 'order',
                  width: 50
                },
                {
                  field: 'tablesToSync',
                  width: 250,
                  mapId: record => {
                    const values = [];
                    if (record.tablesToSync) {
                      record.tablesToSync.forEach((tableDetail: DBTableDetail) => {
                        values.push(tableDetail.tableNames);
                      });
                    }
                    return values.join(', ');
                  }
                },
                {
                  field: 'dayToSyncSt',
                  type: 'DATETIME',
                  format: 'dd/MM/yyyy',
                  width: 180
                },
                {
                  field: 'originDb',
                  mapId: record => {
                    if (record.originDb) {
                      return record.originDb.dbUrl;
                    }
                    return '';
                  }
                },
                {
                  field: 'destinationDbs',
                  mapId: record => {
                    const values = [];
                    if (record.destinationDbs) {
                      record.destinationDbs.forEach((dbDetail: ScheduleProfile.DbDetail) => {
                        values.push(dbDetail.dbUrl);
                      });
                    }
                    return values.join(', ');
                  }
                },
              ],
              fields: [
                {
                  group: '',
                  fields: [
                    {
                      field: 'active',
                      column: 2,
                      type: TYPE.CHECKBOX,
                    },
                    {
                      field: 'title',
                      required: true,
                      column: 4,
                      type: TYPE.TEXT,
                    },
                    {
                      field: 'order',
                      column: 2,
                      type: TYPE.INTEGER
                    },
                    {
                      field: 'dayToSyncSt',
                      column: 4,
                      type: TYPE.DATE,
                      changed: (event, event2) => {
                        if(event2.dayToSyncSt) {
                          const [day, month, year] = event2.dayToSyncSt.split('/');
                          // const date = new Date(+year, +month - 1, +day);
                          event2.dayToSync = new Date(Date.UTC(+year, +month - 1, +day, 0,0,0));
                        }
                      }
                      // format: 'yy-mm-dd',
                    },
                    /////////// tablesToSync
                    {
                      field: 'tablesToSync',
                      column: 12,
                      type: TYPE.RICHFIELD,
                      richFieldConfig: this.getDatabaseDetailRichFieldConfig()
                    },
                    ////////////// originDb
                    {
                      field: 'originDb',
                      column: 12,
                      type: TYPE.SINGLERICHFIELD,
                      richFieldConfig: this.getDBDetailRichFieldConfig()
                    },
                    ////////////// destinationDbs
                    {
                      field: 'destinationDbs',
                      column: 12,
                      type: TYPE.RICHFIELD,
                      richFieldConfig: this.getDBDetailRichFieldConfig()
                    }]
                }]
            }
          },
        ]
      }
    ];
  }

  private getDatabaseDetailRichFieldConfig() {
    return {
      fieldId: 'title',
      fieldName: 'title',
      tableConfigs: [
        {
          field: 'active',
          width: 50,
          mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
        },
        {
          field: 'title',
          width: 250
        },
        {
          field: 'dbUrl',
          width: 250
        },
        {
          field: 'tableNames',
          mapId: record => {
            return record.tableNames ? record.tableNames.join(', ') : '';
          }
        },
      ],
      fields: [
        {
          group: '',
          fields: [
            {
              field: 'active',
              column: 2,
              type: TYPE.CHECKBOX,
            },
            {
              field: 'title',
              required: true,
              column: 4,
              type: TYPE.TEXT,
            },
            {
              field: 'order',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              column: 4,
              type: TYPE.NONE
            },
            {
              field: 'dbDriver',
              column: 4,
              type: TYPE.TEXT
            },
            {
              field: 'dbUrl',
              column: 4,
              type: TYPE.TEXT
            },
            {
              field: 'dbUsername',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'dbPassword',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'tableNames',
              column: 9,
              type: TYPE.MULTITEXT
            },
            {
              field: 'dateColumn',
              column: 3,
              type: TYPE.TEXT
            },
            {
              field: 'customSelectWherePart',
              column: 12,
              type: TYPE.TEXTAREA
            }]
        }]
    };
  }

  private getDBDetailRichFieldConfig() {
    return {
      fieldId: 'title',
      fieldName: 'title',
      tableConfigs: [
        {
          field: 'active',
          width: 50,
          mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
        },
        {
          field: 'title',
          width: 250
        },
        {
          field: 'dbUrl',
          width: 250
        },
        {
          field: 'tableNames',
          mapId: record => {
            return record.tableNames ? record.tableNames.join(', ') : '';
          }
        },
      ],
      fields: [
        {
          group: '',
          fields: [
            {
              field: 'active',
              column: 2,
              type: TYPE.CHECKBOX,
            },
            {
              field: 'title',
              required: true,
              column: 4,
              type: TYPE.TEXT,
            },
            {
              field: 'order',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              column: 4,
              type: TYPE.NONE
            },
            {
              field: 'dbDriver',
              column: 4,
              type: TYPE.TEXT
            },
            {
              field: 'dbUrl',
              column: 4,
              type: TYPE.TEXT
            },
            {
              field: 'dbUsername',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'dbPassword',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'filters',
              column: 12,
              type: TYPE.RICHFIELD,
              richFieldConfig: {
                fieldId: 'column',
                fieldName: 'column',
                tableConfigs: [
                  {
                    field: 'column',
                    width: 250
                  },
                  {
                    field: 'values',
                    mapId: record => {
                      return record.values ? record.values.join(', ') : '';
                    }
                  },
                ],
                fields: [
                  {
                    group: '',
                    fields: [
                      {
                        field: 'column',
                        column: 12,
                        type: TYPE.TEXT
                      },
                      {
                        field: 'values',
                        column: 12,
                        type: TYPE.MULTITEXT
                      }
                    ]
                  }]
              }
            },
            {
              field: 'tableNames',
              column: 12,
              type: TYPE.MULTITEXT
            }]
        }]
    };
  }

  private getArgLogsAnalyzeRichFieldConfig() {
    return {
      fieldId: 'title',
      fieldName: 'title',
      tableConfigs: [
        {
          field: 'active',
          width: 50,
          mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.active?'fa-check':'fa-xmark')+'"/>'
        },
        {
          field: 'title',
          width: 250
        },
        {
          field: 'paths',
          width: 250    ,
          mapId: record => {
            return record.paths ? record.paths.join(', ') : '';
          }
        },
        {
          field: 'scanMode',
          width: 250
        },
        {
          field: 'recursive',
          width: 50,
          mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.recursive?'fa-check':'fa-xmark')+'"/>'
        },
        {
          field: 'includeFileNameRegex',
          width: 250
        },
        {
          field: 'excludeFileNameRegex',
          width: 250
        },
      ],
      fields: [
        {
          group: '',
          fields: [
            {
              field: 'active',
              column: 2,
              type: TYPE.CHECKBOX,
            },
            {
              field: 'title',
              required: true,
              column: 4,
              type: TYPE.TEXT,
            },
            {
              field: 'order',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              column: 4,
              type: TYPE.NONE
            },
            {
              field: 'paths',
              column: 6,
              type: TYPE.MULTITEXT
            },
            {
              field: 'scanMode',
              column: 2,
              type: TYPE.DROPDOWN,
              options: () => this.logScanModes
            },
            {
              field: 'recursive',
              column: 2,
              type: TYPE.CHECKBOX
            },
            {
              column: 2,
              type: TYPE.NONE
            },
            {
              field: 'includeFileNameRegex',
              column: 6,
              type: TYPE.TEXT
            },
            {
              field: 'excludeFileNameRegex',
              column: 6,
              type: TYPE.TEXT
            },
            {
              field: 'encoding',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'startAtEndOnFirstSeen',
              column: 2,
              type: TYPE.CHECKBOX
            },
            {
              field: 'stateDir',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'stateKey',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'resetState',
              column: 2,
              type: TYPE.CHECKBOX
            },
            {
              field: 'maxBytesPerFilePerRun',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'maxLinesPerFilePerRun',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'maxBytesBack',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'cutoffMinutes',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'timestampRegex',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'timestampFormat',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'mergeStackTraces',
              column: 2,
              type: TYPE.CHECKBOX
            },
            {
              field: 'maxStackTraceLines',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'failIfNoFiles',
              column: 2,
              type: TYPE.CHECKBOX
            },
            {
              field: 'exitCodeNoFiles',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'workingDir',
              column: 6,
              type: TYPE.TEXT
            },
            {
              field: 'rules',
              column: 12,
              type: TYPE.RICHFIELD,
              richFieldConfig: {
                fieldId: 'id',
                fieldName: 'id',
                tableConfigs: [
                  {
                    field: 'id',
                    width: 250
                  },
                  {
                    field: 'title',
                    width: 250
                  },
                  {
                    field: 'regex',
                    width: 250
                  },
                  {
                    field: 'excludeRegexes',
                    mapId: record => {
                      return record.values ? record.values.join(', ') : '';
                    }
                  },
                  {
                    field: 'caseInsensitive',
                    width: 250,
                    mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.caseInsensitive?'fa-check':'fa-xmark')+'"/>'
                  },
                  {
                    field: 'failOnMatch',
                    width: 250,
                    mapId: record => '&nbsp;&nbsp;&nbsp;<i class="fas '+(record.failOnMatch?'fa-check':'fa-xmark')+'"/>'
                  },
                  {
                    field: 'exitCodeOnMatch',
                    width: 250,
                  },
                  {
                    field: 'minMatches',
                    width: 250,
                  },
                ],
                fields: [
                  {
                    group: '',
                    fields: [
                      {
                        field: 'id',
                        column: 4,
                        type: TYPE.TEXT
                      },
                      {
                        field: 'title',
                        column: 4,
                        type: TYPE.TEXT
                      },
                      {
                        field: 'regex',
                        column: 4,
                        type: TYPE.TEXT
                      },
                      {
                        field: 'excludeRegexes',
                        column: 12,
                        type: TYPE.MULTITEXT
                      },
                      {
                        field: 'caseInsensitive',
                        column: 2,
                        type: TYPE.CHECKBOX
                      },
                      {
                        field: 'failOnMatch',
                        column: 2,
                        type: TYPE.CHECKBOX
                      },
                      {
                        field: 'exitCodeOnMatch',
                        column: 2,
                        type: TYPE.INTEGER
                      },
                      {
                        field: 'minMatches',
                        column: 2,
                        type: TYPE.INTEGER
                      },
                      {
                        field: 'stopOnTrigger',
                        column: 2,
                        type: TYPE.CHECKBOX
                      },
                      {
                        field: 'maxExamples',
                        column: 2,
                        type: TYPE.INTEGER
                      }
                    ]
                  }]
              }
            }]
        }]
    };
  }

  protected initData() {
    super.initData();

    this.formLoading = true;
    // (this.crudService as InternalUserViewService).getTemplates().subscribe(apiValidation => {
    //   if (apiValidation.success) {
    //     this.templates = apiValidation.item ? [...this.getFields(apiValidation.item)] : [];
    //   } else {
    //     this.errorMessage(apiValidation.message);
    //   }
    //   this.formLoading = false;
    // });
    this.alertTypes = [];
    // tslint:disable-next-line:forin
    for (const key in ScheduleProfile.ALERT_TYPE) {
      this.alertTypes.push(new FieldRecord(key, key.toLowerCase()));
    }

    this.logScanModes = [];
    // tslint:disable-next-line:forin
    for (const key in ScheduleProfile.LogScanMode) {
      this.logScanModes.push(new FieldRecord(key, key.toLowerCase()));
    }

    this.formLoading = false;
    // init new record Object
    this.newRecord = new ScheduleProfile();

    this.settingsFormLoadingProgressValue = 0;

    (this.crudService as SchedulerService).loadSettings().subscribe(apiFileResponse => {
      if (apiFileResponse.success) {
        this.settings = apiFileResponse.item;
      } else {
        this.errorMessage(apiFileResponse.message);
      }

      this.settingsFormLoadingProgressValue = 100;
      this.tableLoading = false;
    });
  }

  private activate(record: ScheduleProfile) {
    this.formRecord = record;
    this.formRecord.active = !this.formRecord.active;
    this.save();
  }

  prepareRecordForCreate() {
    this.newRecord = new ScheduleProfile();
    // if (this.formRecord)
    //   this.formRecord.sortingNumber = this.records ? Math.max(...this.records.map(o => o.sortingNumber)) + 1 : 0;
    this.formRecord.originTitle = undefined;
  }

  prepareRecordForEdit() {
    if (this.formRecord.argDBSyncs) {
      this.formRecord.argDBSyncs.forEach(value => {
        if (value.dayToSync)
          value.dayToSyncSt = dateFormat(value.dayToSync, "dd/mm/yyyy");
      })
    }
  }

  prepareRecordForClone() {
    this.formRecord.originTitle = undefined;
    if (this.formRecord.argDBSyncs) {
      this.formRecord.argDBSyncs.forEach(value => {
        if (value.dayToSync)
          value.dayToSyncSt = dateFormat(value.dayToSync, "dd/mm/yyyy");
      })
    }
  }

  private getSettingsFormConfig() {
    return [
      {
        group: '',
        fields: [
          {
            field: 'alertAll',
            column: 3,
            type: TYPE.CHECKBOX,
          },
          {
            field: 'alertAllLimitOneADay',
            column: 3,
            type: TYPE.CHECKBOX,
          },
          {
            column: 6,
            type: TYPE.NONE,
          },
          {
            field: 'alertAllType',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.alertTypes
          },
          {
            field: 'alertAllExpectedExitCodes',
            column: 4,
            type: TYPE.MULTITEXT,
          },
          {
            field: 'alertAllRecipients',
            column: 12,
            type: TYPE.MULTITEXT,
          },
          {
            field: 'alertMailTemplate',
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'defaultAlertSubject',
            column: 9,
            type: TYPE.TEXTAREA,
          }]
      }];

  }


  // validateForm(): string {
  //   return undefined;
  //   // if (!this.formRecordInForm) {
  //   //   return undefined;
  //   // }
  //   // let message;
  //   // this.formRichFieldConfig.fields.forEach(config => {
  //   //   if (!message) {
  //   //     const fieldMessage = this.validateField(config);
  //   //     if (fieldMessage) {
  //   //       message = fieldMessage;
  //   //     }
  //   //   }
  //   // });
  //   // return message;
  // }
  //
  // validateField(fieldConfig: FormFieldConfig): string {
  //   return undefined;
  //   // if (!this.formRecordInForm) {
  //   //   return '';
  //   // }
  //   // let message;
  //   // if (fieldConfig.required
  //   //   && (!this.formRecordInForm[fieldConfig.field] || (this.formRecordInForm[fieldConfig.field] + '').length === 0)) {
  //   //   message = this.getTranslation('page.' + this.transPartKey + '.form.' + fieldConfig.field) + ' '
  //   //     + this.getTranslation('common.form.required_sing');
  //   // } else if (fieldConfig.validate) {
  //   //   message = fieldConfig.validate(this.formRecordInForm);
  //   // }
  //   // return message;
  // }

  protected logMessage(message: string) {
    if (message)
      this.messageService.add({
        key: 'log',
        severity: 'info',
        icon: 'fas fa-info-circle',
        // summary: this.getTranslation('common.message.info'),
        detail: message,
        // life: 3000
      });
  }

  // save() {
  //   this.formRecord.argDBSyncs.forEach(value => {
  //     if (value.dayToSync) {
  //       const [month, day, year] = (value.dayToSync+'').split('/');
  //       const date = new Date(+year, +month - 1, +day);
  //       value.dayToSync = dateFormat(date, "yyyy-mm-dd");
  //     }
  //   })
  //   super.save();
  // }
}
